import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  IconButton,
  TextField,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Components } from 'react-markdown';
import { ActivityDisplayProps } from '../../types/lessonPlan.types';

// Fix LaTeX math notation - handle various LaTeX patterns for proper rendering
// Converts to $...$ format for inline math and $$...$$ for block math (remark-math standard)
function fixLatexNotation(text: string): string {
  if (!text) return text;
  let fixed = text;
  
  // Step 0: Fix malformed LaTeX expressions (mixed delimiters, incomplete expressions)
  // Fix patterns like: $$...$ or $...$$ or $$...$$$ (extra delimiters)
  fixed = fixed.replace(/\$\$([^$]*)\$\$\$/g, '$$$1$$'); // $$...$$$ -> $$...$$
  fixed = fixed.replace(/\$\$([^$]*)\$/g, '$$$1$$'); // $$...$ -> $$...$$
  fixed = fixed.replace(/\$([^$]*)\$\$/g, '$$$1$$'); // $...$$ -> $$...$$
  
  // Fix incomplete expressions like: $ \frac{3}{1} \times = $ (missing right side)
  fixed = fixed.replace(/\$\s*([^$]*)\s*=\s*\$/g, (match, left) => {
    // If there's an incomplete expression, try to fix it
    if (left.includes('\\times') && !left.includes('=')) {
      return `$${left.trim()}$`; // Just keep the left side for now
    }
    return match;
  });
  
  // Step 1: Convert block math \[ ... \] to $$ ... $$ (remark-math format)
  fixed = fixed.replace(/\\\[([^\]]*)\\\]/g, (match, content) => {
    return `$$${content.trim()}$$`;
  });
  
  // Also handle [ \ ... ] -> $$ ... $$
  fixed = fixed.replace(/\[\s*\\([^\]]*)\\\s*\]/g, (match, content) => {
    return `$$${content.trim()}$$`;
  });
  
  // Step 2: Convert inline math \( ... \) to $ ... $ (remark-math format)
  // This is the standard format that remark-math expects
  fixed = fixed.replace(/\\\(([^)]*)\\\)/g, (match, content) => {
    return `$${content.trim()}$`;
  });
  
  // Step 3: Fix inline math: Convert parentheses notation with LaTeX commands to $ ... $
  // Pattern: ( \frac{...} ... ) or ( \div ... ) etc. -> $ ... $
  // Only convert if it contains LaTeX commands (frac, div, times, sqrt, etc.)
  fixed = fixed.replace(/\(([^)]*(?:\\frac|\\div|\\times|\\sqrt|\\pm|\\leq|\\geq|\\neq|\\approx|\\sum|\\prod|\\int|\\partial)[^)]*)\)/g, (match, content) => {
    // Check if it's already properly delimited with $ or $$
    if (match.startsWith('$') || match.startsWith('\\(')) {
      return match;
    }
    return `$${content.trim()}$`;
  });
  
  // Step 4: Clean up any remaining malformed patterns
  // Remove standalone $ or $$ without content (but preserve valid delimiters)
  fixed = fixed.replace(/\$\s+\$/g, ' '); // $ $ -> (space)
  fixed = fixed.replace(/\$\$\s+\$\$/g, ' '); // $$ $$ -> (space)
  
  return fixed;
}

// Normalize raw activity markdown to ensure valid structure
function normalizeActivityMarkdown(raw: string): string {
  if (!raw) return '';
  let s = String(raw);

  // Fix LaTeX notation first
  s = fixLatexNotation(s);

  // Ensure proper spacing for headings and lists (but preserve numbered list structure)
  // Only add newlines before headings, numbered lists, or bullet lists when missing
  s = s.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2'); // Headings
  s = s.replace(/([^\n])\n(\d+\.\s)/g, '$1\n\n$2'); // Numbered lists (preserve numbers)
  s = s.replace(/([^\n])\n([-*]\s)/g, '$1\n\n$2'); // Bullet lists

  // Convert dash bullets with weird spacing to proper hyphen-space
  s = s.replace(/\n\s*[•]\s*/g, '\n- ');

  // Convert underscore blanks to consistent underline marker
  s = s.replace(/_{3,}/g, '______________');

  // Trim trailing whitespace from lines but preserve structure
  s = s.replace(/[ \t]+$/gm, '');
  return s.trim();
}

// Helper to safely render children from ReactMarkdown (handles strings, arrays, objects)
const safeRenderChildren = (children: React.ReactNode): React.ReactNode => {
  if (typeof children === 'string' || typeof children === 'number') {
    return children;
  }
  if (Array.isArray(children)) {
    return children.map((child, idx) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return child;
      }
      if (React.isValidElement(child)) {
        return <React.Fragment key={idx}>{child}</React.Fragment>;
      }
      // Handle objects that might be passed
      if (child && typeof child === 'object' && child !== null && !Array.isArray(child)) {
        // Try to extract meaningful content - use type guard for safety
        const childObj = child as unknown as Record<string, unknown>;
        if ('props' in childObj && childObj.props && typeof childObj.props === 'object' && childObj.props !== null) {
          const props = childObj.props as Record<string, unknown>;
          if ('children' in props) {
            return <React.Fragment key={idx}>{safeRenderChildren(props.children as React.ReactNode)}</React.Fragment>;
          }
        }
        return null;
      }
      return null;
    });
  }
  if (React.isValidElement(children)) {
    return children;
  }
  // If it's an object, try to extract content or return null
  if (children && typeof children === 'object' && children !== null && !Array.isArray(children)) {
    const childrenObj = children as unknown as Record<string, unknown>;
    if ('props' in childrenObj && childrenObj.props && typeof childrenObj.props === 'object' && childrenObj.props !== null) {
      const props = childrenObj.props as Record<string, unknown>;
      if ('children' in props) {
        return safeRenderChildren(props.children as React.ReactNode);
      }
    }
    return null;
  }
  return children;
};

// Custom markdown components for activities with structured styling
const activityMarkdownComponents: Partial<Components> = {
  h1: ({ children }) => (
    <Typography 
      variant="h5" 
      component="h1"
      sx={{ 
        fontWeight: 600, 
        mb: 2, 
        color: 'primary.main',
        borderBottom: '2px solid',
        borderColor: 'primary.light',
        pb: 1
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography 
      variant="h6" 
      component="h2"
      sx={{ 
        fontWeight: 600, 
        mb: 1.5, 
        color: 'primary.main',
        borderBottom: '1px solid',
        borderColor: 'primary.light',
        pb: 0.5
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography 
      variant="subtitle1" 
      component="h3"
      sx={{ 
        fontWeight: 600, 
        mb: 1, 
        color: 'secondary.main',
        fontSize: '1.1rem'
      }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography 
      variant="subtitle2" 
      component="h4"
      sx={{ 
        fontWeight: 600, 
        mb: 0.5, 
        color: 'text.primary',
        fontSize: '1rem'
      }}
    >
      {children}
    </Typography>
  ),
  h5: ({ children }) => (
    <Typography 
      variant="body1" 
      component="h5"
      sx={{ 
        fontWeight: 600, 
        mb: 0.5, 
        color: 'text.primary',
        fontSize: '0.95rem'
      }}
    >
      {children}
    </Typography>
  ),
  h6: ({ children }) => (
    <Typography 
      variant="body2" 
      component="h6"
      sx={{ 
        fontWeight: 600, 
        mb: 0.5, 
        color: 'text.secondary',
        fontSize: '0.9rem'
      }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography 
      variant="body1" 
      component="p"
      paragraph 
      sx={{ 
        lineHeight: 1.6,
        mb: 1.5
      }}
    >
      {safeRenderChildren(children)}
    </Typography>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="em" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
      {children}
    </Box>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 3, mb: 2, '& > li': { mb: 0.5 } }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 3, mb: 2, '& > li': { mb: 0.5 } }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ mb: 0.5, lineHeight: 1.6 }}>
      {safeRenderChildren(children)}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box 
      component="blockquote"
      sx={{ 
        borderLeft: '4px solid',
        borderColor: 'info.light',
        pl: 2,
        py: 1,
        mb: 2,
        backgroundColor: 'info.lighter',
        fontStyle: 'italic'
      }}
    >
      {children}
    </Box>
  ),
  // Custom handling for activity sections
  hr: () => (
    <Divider sx={{ my: 2 }} />
  ),
  table: ({ children }) => (
    <Box component="div" sx={{ overflowX: 'auto', my: 2 }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
        {children}
      </Box>
    </Box>
  ),
  th: ({ children }) => (
    <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', py: 1, pr: 2 }}>
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box component="td" sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1, pr: 2 }}>
      {children}
    </Box>
  ),
  code: ({ className, children }) => {
    const isInline = !className;
    if (isInline) {
      return (
        <Box
          component="code"
          sx={{
            backgroundColor: 'grey.200',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: '0.9em',
            fontFamily: 'monospace'
          }}
        >
          {children}
        </Box>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          display: 'block',
          backgroundColor: 'grey.100',
          p: 2,
          borderRadius: 1,
          fontSize: '0.9em',
          fontFamily: 'monospace',
          overflowX: 'auto',
          my: 2,
          border: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        {children}
      </Box>
    );
  },
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        backgroundColor: 'grey.100',
        p: 2,
        borderRadius: 1,
        fontSize: '0.9em',
        fontFamily: 'monospace',
        overflowX: 'auto',
        my: 2,
        border: '1px solid',
        borderColor: 'grey.300',
        '& code': {
          backgroundColor: 'transparent',
          padding: 0
        }
      }}
    >
      {children}
    </Box>
  )
};

/**
 * ActivityDisplay Component
 * 
 * Displays generated activities with markdown formatting and editing capabilities.
 * Supports both structured parsing and markdown rendering.
 */
const ActivityDisplay: React.FC<ActivityDisplayProps> = ({
  content,
  isStreaming,
  type,
  onEdit
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');

  // Handle edit mode toggle
  const handleEditStart = () => {
    setEditContent(content);
    setIsEditing(true);
  };

  // Handle save edit
  const handleSave = () => {
    if (onEdit) {
      onEdit(editContent);
    }
    setIsEditing(false);
  };

  // Handle cancel edit
  const handleCancel = () => {
    setIsEditing(false);
    setEditContent('');
  };

  // Get title and description based on activity type
  const getActivityInfo = () => {
    switch (type) {
      case 'introduction':
        return {
          title: 'Lesson Introduction Activities',
          description: 'Pre-reading activities to prepare students for the story',
          icon: <PlayCircleOutlineIcon />,
          color: 'primary'
        };
      case 'practice':
        return {
          title: 'Independent Practice Activities',
          description: 'Post-reading activities for skill practice and assessment',
          icon: <AssignmentIcon />,
          color: 'secondary'
        };
      default:
        return {
          title: 'Activities',
          description: 'Learning activities',
          icon: <AssignmentIcon />,
          color: 'primary'
        };
    }
  };

  const activityInfo = getActivityInfo();

  // Show placeholder when no content is available
  if (!content && !isStreaming) {
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 3, 
          mb: 3, 
          textAlign: 'center',
          backgroundColor: 'grey.50',
          border: '2px dashed',
          borderColor: 'grey.300'
        }}
      >
        <AssignmentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {activityInfo.title} will appear here after generation
        </Typography>
      </Paper>
    );
  }

  const normalized = normalizeActivityMarkdown(typeof content === 'string' ? content : '');

  return (
    <Paper elevation={1} sx={{ mb: 3 }}>
      {/* Activity Content */}
      <Box sx={{ p: 3 }}>
        {isEditing ? (
          // Edit Mode
          <Box>
            {/* Add edit button and title for edit mode */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Edit {activityInfo.title}
              </Typography>
            </Box>
            <TextField
              multiline
              rows={10}
              fullWidth
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Enter your activity content..."
              sx={{ mb: 2 }}
            />
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<CancelIcon />}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<SaveIcon />}
                size="small"
              >
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          // Display Mode - Now with ReactMarkdown
          <Box>
            {/* Add edit button in top right corner for display mode */}
            {content && !isEditing && onEdit && (
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  onClick={handleEditStart}
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  sx={{
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  Edit {activityInfo.title}
                </Button>
              </Box>
            )}
            
            {/* Render content with ReactMarkdown */}
            <Box sx={{ '& > *:last-child': { mb: 0 } }}>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm, remarkMath]} 
                rehypePlugins={[rehypeKatex]}
                components={activityMarkdownComponents}
              >
                {normalized}
              </ReactMarkdown>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ActivityDisplay; 